import * as dbAccessor from '@/utils/dbAccessor'
import {getNullFields, deepEqual, Logger, addNumbers, splitProductName, convertTimestampToDateInObj, formatKeywords} from '@/utils/tools'
import router from '@/router'
import { Announcement, Package } from '@/orm'
import { subDays } from 'date-fns'
import { LabelStatistics } from '@/orm/models/LabelStatistics'
import AbnormalStatistics from '@/orm/models/AbnormalStatistics'
import axios from 'axios'
/**
 * @typedef {import('@/utils/dbAccessor').Predicate} Predicate
 */

const initialState = {
  user: null,
  userExtra: null,
  mainAccount: null,
  userLoading: false,
  tenantWorkFor: [],
  tenantRequested: [],
  sites: [],
  tasks: [],
  historyTasks: [],
  warehouseName: null,
  balanceForUser: {
    total: 0,
    pending: 0,
    released: 0
  },
  paymentRequests: [],
  allTenantLimitedInfo: {},
  allWarehouseLimitedInfo: {},
  paymentMethods: [],
  warehousesForUser: [],
  paymentRequestFilterItems: [
    { name: 'All', method: 0 },
    { name: 'New Comments', method: 1 },
    { name: 'Starred', method: 2 }
  ],
  userInventory: [],
  announcementsMap: new Map(),
  adminAnnouncements: [],
  notifications: [],
  localSettings: {},
  popupStack: [],
  superUsers: [],
  labelStatistics: [],
  abnormalStatistics: [],
  returnPackageCache: [],
  applicationList: [],
  featureApplication: [],
  departments: [],
  commonCommodities: [],
  currentTask: {
    taskForTenant: true,
    warehouse: 'warehouse'
  }
}

const state = Object.assign({}, initialState)

export const mutations = {
  reset_state (state) {
    for (let prop in state) {
      state[prop] = initialState[prop]
    }
  },
  setReturnPkgCache (state, payload) {
    state.returnPackageCache = payload
  },
  setUser (state, payload) {
    state.user = payload
  },
  setMainAccount (state, payload) {
    state.mainAccount = payload
  },
  setUserLoading (state, payload) {
    state.userLoading = payload
  },
  setSites (state, payload) {
    state.sites = payload
  },
  setWarehouseName (state, payload) {
    state.warehouseName = payload
  },
  setTenantWorkFor (state, payload) {
    state.tenantWorkFor = payload
  },
  setExtra (state, payload) {
    state.userExtra = payload
  },
  setTenantRequested (state, payload) {
    state.tenantRequested = payload
  },
  setTasks (state, payload) {
    state.tasks = payload
  },
  setHistoryTasks (state, payload) {
    state.historyTasks = payload
  },
  setSearchedPackages (state, payload) {
    state.searchedPackages = payload
  },
  setConfirmedPackages (state, payload) {
    state.confirmedPackages = payload
  },
  setBalanceForUser (state, payload) {
    state.balanceForUser = payload
  },
  setPaymentRequests (state, payload) {
    state.paymentRequests = payload
  },
  setPaymentMethods (state, payload) {
    state.paymentMethods = payload
  },
  setAllTenantLimitedInfo (state, payload) {
    state.allTenantLimitedInfo = payload
  },
  setAllWarehouseLimitedInfo (state, payload) {
    state.allWarehouseLimitedInfo = payload
  },
  setWarehousesForUser (state, payload) {
    state.warehousesForUser = payload
  },
  setUserInventory (state, payload) {
    state.userInventory = payload
  },
  setAnnouncementsMap (state, payload) {
    state.announcementsMap = payload
  },
  setAdminAnnouncements (state, payload) {
    state.adminAnnouncements = payload
  },
  setNotifications (state, payload) {
    state.notifications = payload
  },
  addNotification (state, payload) {
    state.notifications.push(payload)
  },
  removeNotification (state, payload) {
    state.notifications = state.notifications.filter(({key}) => key !== payload.key)
  },
  setlocalSettings (state, payload) {
    const { name, value } = payload
    state.localSettings[name] = value
    state.user && state.user.uid && window.localStorage.setItem(name + '_' + state.user.uid, JSON.stringify(value))
  },
  registerPopup  (state, payload) {
    state.popupStack.push(payload)
  },
  unRegisterPopup (state, payload) {
    state.popupStack = state.popupStack.filter(item => item !== payload)
  },
  setSuperUsers (state, payload) {
    state.superUsers = payload
  },
  setLabelStatistics (state, payload) {
    state.labelStatistics = payload
  },
  setAbnormalStatistics (state, payload) {
    state.abnormalStatistics = payload
  },
  setFeatureApplication (state, payload) {
    state.featureApplication = payload
  },
  setDepartments (state, payload) {
    state.departments = payload
  },
  setCommonCommodities (state, payload) {
    state.commonCommodities = payload
  },
  setCurrentTask (state, payload) {
    state.currentTask = payload
  }
}

export const actions = {
  reset_state ({commit}) {
    commit('reset_state')
  },
  signUserUp ({commit, dispatch}, payload) {
    commit('setLoading', true)
    commit('clearError')
    payload.overrideKey = 'lkjwnlks23432lksdf$%^$'
    return dbAccessor.callFunction('signupUser', payload)
      .then(uid => {
        return dispatch('signUserIn', {email: payload.email.toLowerCase(), password: payload.password})
      })
      .catch(
        error => {
          commit('setLoading', false)
          commit('setError', error)
        }
      )
  },
  signUserIn ({commit}, payload) {
    commit('setLoading', true)
    commit('clearError')
    return dbAccessor.login(payload.email, payload.password)
      .then(
        user => {
          commit('setLoading', false)
          commit('clearError')
        }
      )
      .catch(
        error => {
          commit('setLoading', false)
          commit('setError', error)
        }
      )
  },
  checkEmailAvailable ({commit, getters}, payload) {
    let predicates = [{
      field: 'email',
      compare: '==',
      value: payload
    }]
    return dbAccessor.queryWithPredicates(predicates, ['users'])
      .then(doc => {
        if (doc.size > 0) {
          return false
        }
        return true
      })
  },
  checkNameAvailable ({commit, getters}, payload) {
    let predicates = [{
      field: 'name',
      compare: '==',
      value: payload
    }]
    return dbAccessor.queryWithPredicates(predicates, ['users'])
      .then(doc => {
        if (doc.size > 0) {
          return false
        }
        return true
      })
  },
  sendPasswordResetEmail ({commit, getters}, payload) {
    return dbAccessor.sendPasswordResetEmail(payload)
  },
  sendEmailVerification ({commit, getters}, payload) {
    return dbAccessor.sendEmailVerification(payload)
  },
  verifySignupEmailAuth ({commit, getters}, email) {
    return dbAccessor.callFunction('auth', {
      email: email,
      actionType: 'verifySignupEmailAuth'
    })
      .then(res => res.data)
  },
  confirmResetPassword ({commit, getters}, payload) {
    const {apiKey, ...rest} = payload
    rest.overrideKey = 'lkjwnlks23432lksdf$%^$'
    return dbAccessor.callFunction('resetPassword', {key: apiKey, ...rest})
  },
  finishEmailSignup ({commit, getters, dispatch}, payload) {
    payload.email = dbAccessor.finishEmailSignup().toLowerCase()
    if (payload.email) {
      return dispatch('signUserUp', payload)
    } else {
      let error = new Error('Invalid Email')
      commit('setError', error)
      return Promise.reject(error)
    }
  },
  async autoSignIn ({commit, dispatch, getters}, payload) {
    try {
      let promises = []
      commit('setLoading', true)
      dispatch('loadLocalSettings')
      if (getters.activeOrganization) {
        promises.push(dispatch('getAllGroups'))
        promises.push(dispatch('getWarehousesForTenant', getters.activeOrganization)
          .then(() => Promise.all([
            dispatch('getWarehouseLimitedInfoForTenant'),
            dispatch('getWarehousesBillingsForTenant'),
            dispatch('getAbnormalPackages')
          ]))
        )
        promises.push(dispatch('getTenantLimitedInfo'))
        promises.push(dispatch('getCommoditiesList', {}))
      } else if (getters.activeWarehouse) {
        promises.push(dispatch('getWarehouseLimitedInfo'))
        promises.push(dispatch('getWaitingScanFormShipments'))
      }
      if (!payload.userData.paymentMethods) {
        let newPayload = {
          paymentMethods: []
        }
        dbAccessor.updateFieldsStore(newPayload, 'users', payload.userData.uid)
      }
      const { orgKey } = payload.userData
      if (orgKey) {
        commit('setOrgKey', orgKey)
      }
      await Promise.all([
        dispatch('loadSystemInfo'),
        dispatch('loadWarehouseInfo'),
        dispatch('loadTenantData')
      ])
      await dispatch('loadWarehouseOrganizations')
      promises.push(dispatch('loadWarehouseSite'))
      promises.push(dispatch('loadWarehouseBillings'))
      promises.push(dispatch('getAllProducts'))
      promises.push(dispatch('getAllTenantLimitedInfo'))
      promises.push(dispatch('getTenantInformation'))
      promises.push(dispatch('loadSysAdminCommonCommoditiesRT'))

      // loading data
      if (!getters.activeWarehouse) {
        dispatch('getAllOffers')
        dispatch('getProposedOffers')
      }

      if (getters.activeOrganization) {
        dispatch('getReportLostTenant')
        dispatch('getPaymentRequestForTenant')
        dispatch('getMembers')
        dispatch('getBalancesForTenant')
        dispatch('getTenantAnnouncements')
        dispatch('loadPackageReportsTenant')
        dispatch('getPendingProductTransfer')
        dispatch('getActiveSupports')
        dispatch('getTenantActiveOrders')
        dispatch('getTenantFulfillmentOrders')
        dispatch('getTenantUploadHistory')
        dispatch('getTenantAddress')
        dispatch('getTenantPackaging')
        dispatch('getTemplates')
        dispatch('getLabelTemplates')
        dispatch('getLabelTemplateDefaults')
        dispatch('getSystemBalance')
        dispatch('getSkuRequests')
        dispatch('getFavorites')
        dispatch('getShipmentDrafts')
        dispatch('getAmazonSites')
        dispatch('getFreightSites')
        dispatch('loadHazmatSites')
        dispatch('getOfflineDeposits')
        dispatch('getStores')
        dispatch('getTenantOrdersTask')
        dispatch('checkToShowReturnPackageNotification')
        dispatch('getCombinedUpcRT')
        const { featureInboundForecast = [], featureInboundForecastPkg = [], featureInboundForecastDropshipping = [] } = getters.tenant || {}
        const isOpenForecastFeature = [...featureInboundForecast, ...featureInboundForecastPkg, ...featureInboundForecastDropshipping].length > 0
        isOpenForecastFeature && dispatch('getForecastHistoryTrackings')
      }
      if (!getters.activeOrganization && !getters.activeWarehouse) {
        dispatch('getReportLostUser')
        dispatch('getPaymentRequestForUser')
        dispatch('getPaymentMethod')
        dispatch('subscribeUserInventory')
        dispatch('getAllTasks')
      }

      if (getters.activeWarehouse) {
        dispatch('getWarehouseAnnouncements')
        dispatch('loadPackageReportsWarehouse')
        dispatch('getActiveSupports')
        dispatch('getAbnormalPackages')
        dispatch('getWarehouseAddress')
        dispatch('getWarehousePackaging')
        dispatch('getTemplates')
        dispatch('getLabelTemplates')
        dispatch('getLabelTemplateDefaults')
        dispatch('getSystemBalance')
        dispatch('getSkuRequests')
        dispatch('getWarehouseUploadHistory')
        dispatch('getInboundTrackingCache')
        dispatch('getAmazonSites')
        dispatch('getFreightSites')
        dispatch('loadHazmatSites')
        dispatch('getPickupRecordRT')
        dispatch('getOfflineDeposits')
        dispatch('loadInboundConfig')
        dispatch('loadUpcPricingPlan')
        dispatch('getOrgProductsInWarehouseRT')
      }
      await Promise.all(promises)
      await dispatch('loadUpc')
      commit('setLoading', false)
      commit('setUserLoading', false)
      dispatch('getAllAnnouncements')
      dispatch('getShipments')
    } catch (error) {
      console.error(error)
    }
  },
  async logout ({commit, dispatch, getters}) {
    await dispatch('flushEventLogs')
    commit('setUser', null)
    let subscribes = getters.subscribed
    // console.log('subscribes: ', Object.keys(subscribes))
    Object.keys(subscribes).forEach(key => { subscribes[key] && subscribes[key]() })
    await dbAccessor.logout()
    await dispatch('reset_state')
    router.push('/')
  },
  async loadUser ({commit, dispatch, getters}, payload) {
    let {uid, email} = payload
    commit('setUserLoading', true)

    const url = new URL(location.href)
    const companyName = decodeURIComponent(atob(url.searchParams.get('name')))

    let staffUser
    let staffEmail = email
    const getAdmin = async () => {
      let adminDoc = await dbAccessor.queryWithPredicatesStore(
        [{field: 'email', compare: '==', value: email}],
        'sysAdmin',
        'general',
        'superusers')
      if (adminDoc.size === 1) {
        payload.isAdmin = true
        let userIdToken = await payload.getIdTokenResult()
        let roles = userIdToken.claims[adminDoc.docs[0].id] || userIdToken.claims.roles || []
        payload.roles = roles
        payload.isSuperUser = adminDoc.docs[0].data().isSuperUser || false
        payload.name = payload.displayName
        dispatch('updateStaffLoginTime', payload)
        commit('setUser', payload)
        commit('setUserLoading', false)
        dispatch('getAdminAnnouncements')
        dispatch('getActiveSupports')
        dispatch('loadHazmatSites')
        dispatch('loadAllWarehouses')
        dispatch('loadSysAdminDepartmentsRT')
        dispatch('loadSysAdminCommonCommoditiesRT')
        return Promise.resolve(true)
      }
    }

    if (url.searchParams.get('name') && companyName && !companyName.includes('--Individual')) {
      const predicates = [{
        field: 'name',
        compare: '==',
        value: companyName
      }]
      const docs = await dbAccessor.queryWithPredicatesStore(predicates, 'users')
      if (docs.size === 0) {
        throw Error('Email address not found.')
      }
      const userDoc = docs.docs[0]
      const mainAccountData = (await dbAccessor.queryStore('users', uid)).data()
      mainAccountData.uid = uid
      if (companyName.split('--Individual')[0] === payload.displayName) {
        const isDone = await getAdmin()
        if (isDone === true) {
          commit('setMainAccount', mainAccountData)
          return
        }
      }
      uid = userDoc.id
      email = userDoc.data().email
      staffUser = Object.assign({}, payload)
      staffUser.uid = uid
      staffUser.email = email
      let userIdToken = await payload.getIdTokenResult()
      mainAccountData.roles = userIdToken.claims.roles || []
      commit('setMainAccount', mainAccountData)
    }
    commit('setUserLoading', true)

    let successFunc = async (doc) => {
      if (!doc.exists) {
        const isDone = await getAdmin()
        if (isDone === true) return
        commit('setUserLoading', false)
        commit('setLoading', false)
        throw Error('user doesnot exists')
      }
      let userData = Object.assign({}, {uid}, doc.data())
      let user

      // load user dynamically based on it is real login or admin register user
      if (payload.getIdTokenResult && !url.searchParams.get('name')) {
        // this is regular login
        user = payload
        let userIdToken = await user.getIdTokenResult()
        user.roles = userIdToken.claims.roles || []
        const sysRoles = [ 'sysAdmin', 'sysAdminStaff', 'sysAdminOverseaOperator', 'sysAdminReport', 'sysAdminDepartmentSale', 'sysAdminFinance' ]
        if (user.roles.some(role => sysRoles.includes(role))) {
          const isDone = await getAdmin()
          if (isDone === true) {
            commit('setMainAccount', userData)
            return
          }
        }
      } else if (url.searchParams.get('name')) {
        user = staffUser || payload
        const { role } = doc.data()
        switch (role) {
          case 0:
            const { users: tenantUsers } = (await dbAccessor.queryStore('tenants', userData.organizations[0])).data()
            let tenantUser = tenantUsers.find(({email}) => email === staffEmail)
            if (!tenantUser) {
              window.location.search = ''
            }
            user.roles = tenantUser.roles
            break
          case 1:
            delete userData.organizations
            delete userData.warehouses
            user.roles = ['user']
            break
          case 2:
            const { users: warehouseUsers } = (await dbAccessor.queryStore('warehouses', userData.warehouses[0])).data()
            let warehouseUser = warehouseUsers.find(({email}) => email === staffEmail)
            if (!warehouseUser) {
              window.location.search = ''
            }
            user.roles = warehouseUser.roles
            break
        }
      } else {
        // this is admin register as other user.
        // we only change uid and email, keep the roles
        user = Object.assign({}, payload.user)
        user.uid = payload.uid
        user.email = payload.email
        const {role = 1} = userData
        switch (role) {
          case 0:
            user.roles = ['orgOwner']
            break
          case 1:
            if (userData.warehouses && userData.warehouses[0]) {
              const {users: warehouseUsers} = (await dbAccessor.queryStore('warehouses', userData.warehouses[0])).data()
              const {roles = []} = (warehouseUsers.find(({key}) => key === uid) || {})
              user.roles = roles
            } else if (userData.organizations && userData.organizations[0]) {
              const {users: orgUsers} = (await dbAccessor.queryStore('tenants', userData.organizations[0])).data()
              const {roles = []} = (orgUsers.find(({key}) => key === uid) || {})
              user.roles = roles
            } else {
              user.roles = ['user']
            }
            break
          case 2:
            user.roles = ['warehouseOwner']
            break
        }
      }
      user.name = user.displayName || user.name || userData.name
      if (getters.user) {
        dispatch('updateStaffLoginTime', payload)
        let shouldReloadTenantInformation = userData.workfor && (getters.tenantWorkFor.length === 0 || !deepEqual(userData.workfor, getters.activeWorkfor))
        // handle warehouse worker change
        let shouldReloadAfterWarehouseChange = !deepEqual(userData.warehouses, getters.userExtra.warehouses)
        // handle organization worker change
        let shouldReloadAfterOrganizationChange = !deepEqual(userData.organizations, getters.userExtra.organizations)
        commit('setExtra', userData)
        commit('setUser', user)
        commit('setLoading', false)

        if (shouldReloadAfterWarehouseChange || shouldReloadAfterOrganizationChange) {
          dispatch('logout')
          dispatch('reset_state')
          router.push('/')
        }
        return shouldReloadTenantInformation ? dispatch('getTenantInformation') : Promise.resolve('finished')
      } else {
        commit('setExtra', userData)
        commit('setUser', user)
        commit('setLoading', false)
        return dispatch('autoSignIn', {user, userData})
      }
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setUserLoading', false)
      commit('setLoading', false)
      throw error
    }

    let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, [], 'users', uid)
    commit('addTosubscribed', {user: unsubscribe})
  },
  async updateStaffLoginTime ({ commit, getters }, payload) {
    const { uid } = payload
    const self = await dbAccessor.queryStore('users', uid)
    if (!self.exists) return
    if (payload.role === undefined) return
    const { accounts = [] } = self.data()
    let vendorKey = null
    if (accounts.length === 0) {
      vendorKey = getters.activeOrganization || getters.activeWarehouse
      if (!vendorKey) return
    }
    const roleMap = {
      0: 'tenants',
      2: 'warehouses'
    }
    const time = dbAccessor.getServerTimestamp()
    await dbAccessor.runInTransactionStore(async transaction => {
      if (accounts.length) {
        const accountsDocs = await Promise.all(accounts.map(async account => {
          const doc = {
            ref: null,
            users: []
          }
          if (account.isAdmin) {
            const adminDoc = await transaction.get(dbAccessor.buildStoreQuery(['sysAdmin', 'general', 'superusers', account.key]))
            if (!adminDoc.exists) return
            doc.users.push(adminDoc.data())
            doc.ref = adminDoc.ref
          } else {
            const vendorDoc = await transaction.get(dbAccessor.buildStoreQuery([roleMap[account.role], account.key]))
            if (!vendorDoc.exists) return
            const { users: vendorUsers = [] } = vendorDoc.data()
            if (!vendorUsers.length) return
            doc.users.push(...vendorUsers)
            doc.ref = vendorDoc.ref
          }
          return doc
        }))
        accountsDocs.forEach(doc => {
          const { ref, users } = doc || {}
          if (!ref) return
          const index = users.findIndex(({ key }) => key === uid)
          if (index === -1) return
          users[index].lastLoginTime = time
          if (ref.path.includes('superusers')) {
            transaction.update(ref, {lastLoginTime: time})
          } else {
            transaction.update(ref, { users: users })
          }
        })
      } else {
        const vendorDoc = await transaction.get(dbAccessor.buildStoreQuery([roleMap[payload.role], vendorKey]))
        if (!vendorDoc.exists) return
        const { users: vendorUsers = [] } = vendorDoc.data()
        if (!vendorUsers.length) return
        const user = vendorUsers.find(({ key }) => key === uid)
        if (!user) return
        user.lastLoginTime = time
        transaction.update(vendorDoc.ref, { users: vendorUsers })
      }
    })
  },
  loadLocalSettings ({commit, getters}, payload) {
    let isEscClose = window.localStorage.getItem('isEscClose_' + getters.uid)
    isEscClose = isEscClose !== 'false'
    commit('setlocalSettings', {name: 'isEscClose', value: isEscClose})
  },
  requestJoin ({commit, getters}, payload) {
    const {requestCode, note} = payload
    let predicates = [{
      field: 'invitationCode',
      compare: '==',
      value: requestCode
    }]
    return dbAccessor.queryWithPredicates(predicates, ['tenants'])
      .then(docs => {
        if (docs.size > 1) return Promise.reject(Error('Deplicate invitation code. Contact support!'))
        if (docs.size === 0) return Promise.reject(Error('Invalid request code. Contact Organization!'))
        let doc = docs.docs[0]
        let tenant = {
          TenantID: doc.id,
          tenantName: doc.data().name
        }

        if (getters.tenantsForUser.some(item => item.tenantKey === tenant.TenantID || item.TenantID === tenant.TenantID)) {
          return Promise.reject(new Error('Already sent request to this organization.'))
        }
        let newPayload = {
          uid: getters.uid,
          name: getters.userExtra.name,
          email: getters.userExtra.email,
          TenantID: tenant.TenantID,
          tenantName: tenant.tenantName,
          note
        }
        return dbAccessor.insertStore(newPayload, 'requests')
      })
  },
  getTenantInformation ({commit, getters}) {
    if (!getters.activeWorkfor) return

    let activeWorkfor = Object.keys(getters.activeWorkfor)
    activeWorkfor = activeWorkfor.filter((item) => {
      return getters.activeWorkfor[item] >= 2
    })
    let companies = []
    let promises = activeWorkfor.map((item) => {
      return dbAccessor.queryStore('tenantLimitedInfo', item)
        .then(doc => {
          let {name, companyName, pendingPeriod} = doc.data()
          let company = {
            tenantKey: item,
            status: 'accepted',
            tenantName: name || companyName,
            pendingPeriod: pendingPeriod
          }
          companies.push(company)
        })
    })
    return Promise.all(promises).then(() => {
      commit('setTenantWorkFor', companies)
    })
  },
  getTenantRequested ({commit, getters}) {
    let successFunc = (docs) => {
      let tenantRequested = []
      docs.forEach(doc => {
        const obj = doc.data()
        const tenantRequest = {
          TenantID: obj.TenantID,
          tenantName: obj.tenantName,
          status: 'pending',
          requestID: doc.id
        }
        tenantRequested.push(tenantRequest)
      })

      commit('setTenantRequested', tenantRequested)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let predicates = [{
      field: 'uid',
      compare: '==',
      value: getters.uid
    }]

    let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, predicates, 'requests')
    commit('addTosubscribed', {requests: unsubscribe})
  },
  clearTenantRequested ({getters, commit}) {
    getters.subscribed.requests && getters.subscribed.requests()
    let data = 'requests'
    commit('clearSubscribed', {data, initValues: initialState[data]})
  },
  cancelRequest ({commit, getters}, payload) {
    dbAccessor.removeStore('requests', payload)
    let tenantRequested = getters.tenantRequested
    for (let i = 0; i < tenantRequested.length; i++) {
      if (tenantRequested[i].requestID === payload) {
        tenantRequested.splice(i, 1)
        tenantRequested = [...tenantRequested]
      }
    }
    commit('setTenantRequested', tenantRequested)
  },
  async takeOffer ({commit, getters, dispatch}, payload) {
    let nullFields = getNullFields(payload)
    if (nullFields.length > 0) {
      let error = `payload contains null value property (${nullFields.join(',')}). Confirm task canceled`
      console.error(error)
      return Promise.reject(Error(error))
    }
    Logger.log('takeOffer', payload)
    let predicates = [{
      field: 'offerKey',
      compare: '==',
      value: payload.offerKey
    },
    {
      field: 'warehouse',
      compare: '==',
      value: payload.warehouse
    },
    {
      field: 'uid',
      compare: '==',
      value: getters.uid
    },
    {
      field: 'price',
      compare: '==',
      value: payload.price
    },
    {
      field: 'bonus',
      compare: '==',
      value: payload.bonus
    }]
    let confirmTaskPayload
    let docs = await dbAccessor.queryWithPredicatesStore(predicates, 'tasks', 'tasks', 'active')
    await dbAccessor.runInTransactionStore(async (transaction) => {
      let offerRef = dbAccessor.buildStoreQuery(['offers', 'offers', 'active', payload.offerKey])
      let offerDoc = await transaction.get(offerRef)

      let theOffer = offerDoc.data()

      if (!theOffer || theOffer.quantity < theOffer.taken + payload.quantity) {
        throw Error('validation-failed')
      }
      let newTaskRef = dbAccessor.buildStoreQuery(['tasks', 'tasks', 'active']).doc()
      let newTask = dbAccessor.addNewDocTimestamp({
        ...payload,
        uid: getters.uid,
        userName: getters.userExtra.name,
        userEmail: getters.userExtra.email,
        searchKeywords: [payload.offerKey, ...splitProductName(payload.productName), getters.userExtra.name]
      })

      if (docs.size > 0) {
        const taskDoc = docs.docs.find(doc => doc.get('isPropose') === false) || docs.docs[0]
        let taskData = taskDoc.data()
        let {quantity, isPropose = false, pendingPackages = [], taskId = ''} = taskData
        if (isPropose) {
          transaction.set(newTaskRef, newTask)
        } else {
          let transactionTaskDoc = await transaction.get(taskDoc.ref)
          const totalQuantity = quantity + payload.quantity
          if (payload.isEnableQtyLimit && payload.acceptQtyLimit && totalQuantity > payload.acceptQtyLimit) throw Error(`quantity-exceeds$${quantity}`)
          transaction.update(transactionTaskDoc.ref, dbAccessor.addNewDocTimestamp({quantity: totalQuantity}))
          if (pendingPackages.length) {
            let exceedPkgs = pendingPackages.filter(pkg => pkg.errorType === 'exceedTaskQuantity').map(pkg => pkg.exceedPkgInfo)
            if (exceedPkgs.length) {
              let uploadExceedPkgs = []
              let remainQty = totalQuantity
              exceedPkgs.forEach(pkg => {
                if (remainQty - pkg.quantity >= 0) {
                  remainQty = remainQty - pkg.quantity
                  uploadExceedPkgs.push(pkg)
                }
              })
              if (uploadExceedPkgs.length) {
                confirmTaskPayload = convertTimestampToDateInObj({...taskData, packages: uploadExceedPkgs, taskKey: taskId, quantity: totalQuantity})
              }
            }
          }
        }
      } else {
        transaction.set(newTaskRef, newTask)
      }
      transaction.update(offerDoc.ref, dbAccessor.addUpdateDocTimestamp({taken: theOffer.taken + payload.quantity}))
    })
    if (confirmTaskPayload) {
      dispatch('confirmTask', confirmTaskPayload)
    }
    return Promise.resolve()
  },
  async getTaskInfo  ({commit, getters}, payload) {
    let { ownTaskId } = payload
    let successFunc = (doc) => {
      let taskData = {...convertTimestampToDateInObj(doc.data()), taskKey: doc.id}
      commit('setCurrentTask', taskData)
      commit('setLoading', false)
    }
    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }
    let predicates = []
    let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, predicates, 'tasks', 'tasks', 'active', ownTaskId)
    commit('addTosubscribed', {currentTask: unsubscribe})
  },
  async addTaskForTenant ({commit, getters}, payload) {
    payload.tenantEmail = getters.userExtra.email
    let taskRef
    await dbAccessor.runInTransactionStore(async transaction => {
      let offerDoc = await transaction.get(dbAccessor.buildStoreQuery(['offers', 'offers', 'active', payload.offerKey]))
      taskRef = await dbAccessor.buildStoreQuery(['tasks', 'tasks', 'active']).doc()
      transaction.update(offerDoc.ref, dbAccessor.addUpdateDocTimestamp({ownTaskId: taskRef.id}))
      transaction.set(taskRef, dbAccessor.addNewDocTimestamp(payload))
    })
    return taskRef.id
  },
  getAllTasks ({commit, getters}, payload) {
    commit('setLoading', true)
    let successFunc = (docs) => {
      let tasks = docs.docs.map(doc => {
        const obj = convertTimestampToDateInObj(doc.data())
        const taskKey = doc.id
        obj.confirmedPackages && obj.confirmedPackages.forEach(item => {
          item.isConfirmed = true
        })
        return {...obj, taskKey}
      })
      commit('setTasks', tasks.filter(task => !task.taskForTenant))
      commit('setLoading', false)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }
    // get all offers for a tenant or for a member
    let predicates = getters.activeOrganization ?
      [{
        field: 'tenantKey',
        compare: '==',
        value: getters.activeOrganization
      }]
      :
      [{
        field: 'uid',
        compare: '==',
        value: getters.uid
      }]
    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, predicates, ['tasks', 'tasks', 'active'], 'createTime', true)
    commit('addTosubscribed', {tasks: unsubscribe})
  },
  getHistoryTasksPagination ({commit, getters}, payload) {
    // get all offers for a tenant or for a member
    let predicates = getters.activeOrganization ?
      [{
        field: 'tenantKey',
        compare: '==',
        value: getters.activeOrganization
      }]
      :
      [{
        field: 'userKey',
        compare: '==',
        value: getters.uid
      }]

    predicates.push({
      field: 'transactionType',
      compare: '==',
      value: 'inbound'
    })

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])

    payload.predicates && payload.predicates.predicateText &&
      (predicates.push({
        field: `searchKeywords`,
        compare: 'array-contains',
        value: payload.predicates.predicateText
      }))

    return dbAccessor.queryWithPagination(predicates, ['transaction'], 'createTime', true, payload && payload.startAfter, payload && payload.limit)
  },
  getCanceledTasksPagination ({commit, getters}, payload) {
    let predicates = getters.activeOrganization ?
      [{
        field: 'tenantKey',
        compare: '==',
        value: getters.activeOrganization
      }]
      :
      [{
        field: 'uid',
        compare: '==',
        value: getters.uid
      }]

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])

    payload.predicates && payload.predicates.predicateText &&
      (predicates.push({
        field: `searchKeywords`,
        compare: 'array-contains',
        value: payload.predicates.predicateText
      }))
    return dbAccessor.queryWithPagination(predicates, ['canceledTasks'], 'createTime', true, payload && payload.startAfter, payload && payload.limit)
  },
  getActiveTasksPagination ({commit, getters}, payload) {
    // get all offers for a tenant or for a member
    let predicates = [{
      field: 'tenantKey',
      compare: '==',
      value: getters.activeOrganization
    }]

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])

    payload.predicates && payload.predicates.predicateText &&
      (predicates.push({
        field: `searchKeywords`,
        compare: 'array-contains',
        value: payload.predicates.predicateText
      }))

    return dbAccessor.queryWithPagination(predicates, ['tasks', 'tasks', 'active'], 'createTime', true, payload && payload.startAfter, payload && payload.limit)
  },
  async cancelTask ({commit, getters}, payload) {
    let {task, cancelQuantity} = payload
    Logger.log('cancelTask', payload)
    const taskDoc = (await dbAccessor.queryStore('tasks', 'tasks', 'active', task.taskKey || task._key))
    let isRemove = task.quantity === cancelQuantity && (!task.pendingPackages?.length)
    if (taskDoc.exists) {
      const { quantity } = taskDoc.data()
      if (quantity < cancelQuantity) throw Error(`The quantity exceeds the quantity of currently available tasks${quantity}`)
    }
    if (getters.activeOrganization) {
      if (taskDoc.exists) {
        task = {...taskDoc.data(), taskKey: taskDoc.id}
        isRemove = task.quantity <= cancelQuantity && (!task.pendingPackages?.length)
      } else {
        return 0
      }
    }
    const operator = getters.operator
    if (task.isPropose) {
      if (isRemove) {
        await dbAccessor.removeStore('tasks', 'tasks', 'active', task.taskKey || task._key)
      } else {
        await dbAccessor.increaseValueInTransactionStore('quantity', -cancelQuantity, ['tasks', 'tasks', 'active', task.taskKey || task._key], undefined, false)
      }
    } else {
      if (isRemove) {
        await dbAccessor.increaseValueAndDeleteDataInTransactionStore('taken', -task.quantity,
          ['offers', 'offers', 'active', task.offerKey], ['tasks', 'tasks', 'active', task.taskKey || task._key], true, false, false)
      } else {
        let promises = []
        promises.push(dbAccessor.increaseValueInTransactionStore('taken', -cancelQuantity, ['offers', 'offers', 'active', task.offerKey], undefined, false))
        promises.push(dbAccessor.increaseValueInTransactionStore('quantity', -cancelQuantity, ['tasks', 'tasks', 'active', task.taskKey || task._key], undefined, false))
        await Promise.all(promises)
      }
    }
    await dbAccessor.insertStore({
      ...task,
      cancelQuantity,
      workerKey: operator.uid,
      workerName: operator.name
    }, 'canceledTasks')
    return (task.quantity - cancelQuantity)
  },
  clearAllTaskData ({getters, commit}) {
    getters.subscribed.tasks && getters.subscribed.tasks()
    let data = 'tasks'
    commit('clearSubscribed', {data, initValues: initialState[data]})
  },
  addNurseAddress ({commit, getters}, payload) {
    dbAccessor.addArrayItemInTransactionStore('address', payload, 'userLimitedInfo', getters.userExtra.uid)
      .then(() => {
        let address = getters.sites
        address.push(payload)
        commit('setSites', address)
      })
  },
  editNurseAddress ({commit, getters}, payload) {
    // payload is {...address}
    let {newAddress, oldAddress} = payload
    return dbAccessor.queryStore('userLimitedInfo', getters.userExtra.uid)
      .then(doc => {
        let address = doc.data().address
        let payloadIndex = address.findIndex(address => address.siteName === oldAddress.siteName)
        if (payloadIndex < 0) throw new Error('Cannot find this site!')
        address[payloadIndex] = newAddress
        let newPayload = { address: address }
        return dbAccessor.updateFieldsStore(newPayload, 'userLimitedInfo', getters.userExtra.uid)
          .then(() => {
            commit('setSites', address)
          })
      })
  },
  subscribeUserInventory ({commit, getters}) {
    let successFunc = (inventoryCols) => {
      let inventory = inventoryCols.size > 0 ? inventoryCols.docs.map(doc => { return {...doc.data(), productKey: doc.id} }) : []
      commit('setUserInventory', inventory)
    }
    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }
    let predicates = [ {field: 'quantity', compare: '>', value: 0} ]
    let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, predicates, 'userLimitedInfo', getters.userExtra.uid, 'inventory')
    commit('addTosubscribed', {userInventory: unsubscribe})
  },
  checkUserDistribution ({getters}, {state, zip, siteName}) {
    let {userInventory} = getters
    let distributionKey = btoa(`${state} - ${zip} - ${siteName}`)
    return !!(
      userInventory.filter(({distribution}) =>
        distribution && distribution[distributionKey] && distribution[distributionKey].quantity
      ).length
    )
  },
  async deleteNurseAddress ({commit, getters, dispatch}, payload) {
    let { reportLost } = getters
    let distributionKey = btoa(`${payload.state} - ${payload.zip} - ${payload.siteName}`)
    // Check report lost
    let countReportLost = (reportLost ? 1 : 0)
      && reportLost.length
      && reportLost.reduce((count, rl) => count + rl.distributionKey === distributionKey ? 1 : 0, 0)
    if (countReportLost > 0) throw new Error('Cannot remove this address because it has unconfirmed report lost!')

    // check distribution inventory
    let checkUserDistribution = await dispatch('checkUserDistribution', payload)
    if (checkUserDistribution) throw new Error('Cannot remove this address because it has inventory!')

    let doc = await dbAccessor.queryStore('userLimitedInfo', getters.userExtra.uid)
    let { address } = doc.data()
    let payloadIndex = address.findIndex(address => address.siteName === payload.siteName)
    if (payloadIndex < 0) throw new Error('Cannot find this site!')
    address.splice(payloadIndex, 1)
    let newPayload = { address: address }
    await dbAccessor.updateFieldsStore(newPayload, 'userLimitedInfo', getters.userExtra.uid)
    let newAddress = getters.sites
    newAddress.splice(payloadIndex, 1)
    commit('setSites', [...address])
  },
  getUserSites ({commit, getters}, payload) {
    return dbAccessor.queryStore('userLimitedInfo', payload || getters.uid)
      .then((doc) => {
        if (doc.exists) {
          let sites = doc.data().address || []
          commit('setSites', sites)
        }
      })
  },
  getWarehouseName ({commit, getters}, payload) {
    dbAccessor.queryStore('warehouses', payload)
      .then((doc) => {
        commit('setWarehouseName', doc.data().name)
      })
  },

  async getPackagesByTrackings ({commit, getters, dispatch}, payload) {
    let {trackingArr, offerKey} = payload
    const doc = await dbAccessor.queryStore('tenants', payload.tenantKey, 'inventory', payload.productId)
    if (!doc.exists) throw Error('This organization is no longer collecting this product.')
    const {upc} = doc.data()
    let duplicateTrackingTasksMap = await dispatch('checkTrackingIsDuplicatedFromTask', {trackings: trackingArr, upc, offerKey})
    let duplicateTrackings = Object.keys(duplicateTrackingTasksMap)
    trackingArr = trackingArr.filter(tracking => !duplicateTrackings.includes(tracking))
    const packagePromises = trackingArr.map(tracking => {
      const predicates = upc ? [
        {
          field: 'trackings',
          compare: 'array-contains',
          value: tracking
        },
        {
          field: 'upc',
          compare: '==',
          value: upc
        }
      ] : [
        {
          field: 'trackings',
          compare: 'array-contains',
          value: tracking
        }
      ]
      return payload.warehouseKeys.map(warehouseKey => dbAccessor.queryWithPredicatesStore(predicates, 'warehouses', warehouseKey, 'packages'))
    })

    const trackings = trackingArr.map(tracking => {
      return payload.warehouseKeys.map(warehouseKey => tracking)
    }).flat(1)

    let packagesDocsArr = await Promise.all(packagePromises.flat(1))
    const matchedTrackings = new Set(packagesDocsArr.reduce((acc, docs, index) => {
      return (docs.empty || docs.docs[0].isAbnormal || docs.docs[0].errorType) ? acc : [...acc, trackings[index]]
    }, []))
    const unmatchedTrackings = trackingArr.filter(tracking => !matchedTrackings.has(tracking))
    // Convert package doc array to packages and remove sub-trackings which point to the same package
    let packages = packagesDocsArr.reduce((acc, docs, index) => [...acc, ...docs.docs.map(doc => {
      return doc.exists && !acc.some(item => item.packageID === doc.id) ? {
        ...doc.data(),
        warehouseKey: doc.ref.path.split('/')[1],
        trackingConfirmed: [trackings[index]],
        packageID: doc.id,
        isTaskTooOldForPkg: subDays(doc.data().createTime.toDate(), 45) > payload.taskCreateTime
      } : {isDupPackage: true, trackingConfirmed: trackings[index], packageID: doc.id}
    })], [])
    // Add sub-trackings to the ConfirmedTackings list
      .reduce((acc, pkg) => {
        if (pkg.isDupPackage) {
          acc.find(item => item.packageID === pkg.packageID).trackingConfirmed.push(pkg.trackingConfirmed)
          return acc
        } else {
          return [...acc, pkg]
        }
      }, [])

    return {unmatchedTrackings, searchedPackages: convertTimestampToDateInObj(packages), duplicateTrackingTasksMap, upc}
  },
  async checkTrackingIsDuplicatedFromTask ({commit, getters}, payload) {
    const {trackings, upc, offerKey} = payload
    let taskPromises = trackings.map(tracking => {
      const predicates = upc ? [
        {
          field: 'trackingNumsArr',
          compare: 'array-contains',
          value: tracking
        },
        {
          field: 'upc',
          compare: '==',
          value: upc
        }
      ] : [
        {
          field: 'trackingNumsArr',
          compare: 'array-contains',
          value: tracking
        },
        {
          field: 'offerKey',
          compare: '==',
          value: offerKey
        }
      ]
      return dbAccessor.queryWithPredicatesStore(predicates, 'tasks', 'tasks', 'active')
    })
    let taskDocsArr = await Promise.all(taskPromises)
    let duplicateTrackingTasksMap = {}
    taskDocsArr.forEach((docs, index) => {
      if (!docs.empty) {
        duplicateTrackingTasksMap[trackings[index]] = convertTimestampToDateInObj(docs.docs[0].data())
      }
    })
    return duplicateTrackingTasksMap
  },
  confirmTask ({commit, getters, dispatch}, payload) {
    let nullfields = getNullFields(payload)
    if (nullfields.length > 0) {
      let error = `payload contains null value property (${nullfields.join(',')}). Confirm task canceled`
      return Promise.reject(Error(error))
    }
    Logger.log('confirmTask', payload)
    payload.uid && (payload.userKey = payload.uid)
    delete payload.warehouseKeys
    delete payload.expirationDate
    payload.createTime = payload.createTime.toISOString()
    payload.lastModifiedTime = payload.lastModifiedTime.toISOString()

    commit('updatePingHistory', 'confirmTask')
    return dbAccessor.callFunction('confirmTask', payload)
      .catch(error => {
        let errorCode = error.message.slice(25).trim()
        if (errorCode === 'quantity-error') {
          throw Error('Package quantity exceed maximum task amount!!!')
        } else if (error.message === 'upc-unmatch') {
          throw Error('Package upc has been changed, please search again.')
        }
        throw error
      })
  },
  async saveTrackings ({commit, getters}, payload) {
    if (payload.upcMissing) {
      delete payload.upcMissing

      return dbAccessor.runInTransactionStore(async transaction => {
        let offerDoc = await transaction.get(dbAccessor.buildStoreQuery(['offers', 'offers', 'active', payload.offerKey]))
        let taskDoc = await transaction.get(dbAccessor.buildStoreQuery(['tasks', 'tasks', 'active', payload.taskId]))

        let taskUnconfirmedTracking = []
        let taskUnconfirmedPackage = []
        payload.pendingPackages.forEach(item => {
          if (item.packageID) {
            taskUnconfirmedPackage.push(item)
          } else {
            taskUnconfirmedTracking.push(item.trackingConfirmed[0])
          }
        })
        // let taskUnconfirmedPackagesAndTracking = payload.pendingPackages.map(pkg => pkg.packageID ? pkg : pkg.trackingConfirmed[0])
        let {unconfirmedPackagesAndTracking = []} = offerDoc.data()
        if (unconfirmedPackagesAndTracking.length >= 5) {
          if (unconfirmedPackagesAndTracking.every(item => item.packageID)) {
            transaction.update(taskDoc.ref, dbAccessor.addUpdateDocTimestamp(payload))
          } else if (taskUnconfirmedPackage.length) {
            unconfirmedPackagesAndTracking = unconfirmedPackagesAndTracking.map(item => {
              return item.packageID ? item : (taskUnconfirmedPackage.length ? taskUnconfirmedPackage.pop() : item)
            })
            transaction.update(offerDoc.ref, dbAccessor.addUpdateDocTimestamp({unconfirmedPackagesAndTracking}))
            transaction.update(taskDoc.ref, dbAccessor.addUpdateDocTimestamp(payload))
          } else {
            transaction.update(taskDoc.ref, dbAccessor.addUpdateDocTimestamp(payload))
          }
        } else {
          unconfirmedPackagesAndTracking = [...unconfirmedPackagesAndTracking, ...taskUnconfirmedTracking, ...taskUnconfirmedPackage]
          unconfirmedPackagesAndTracking.sort((a, b) => {
            if (a.packageID && !b.packageID) {
              return -1
            } else if (!a.packageID && b.packageID) {
              return 1
            } else {
              return 0
            }
          })
          unconfirmedPackagesAndTracking = unconfirmedPackagesAndTracking.slice(0, 5)
          transaction.update(offerDoc.ref, dbAccessor.addUpdateDocTimestamp({unconfirmedPackagesAndTracking}))
          transaction.update(taskDoc.ref, dbAccessor.addUpdateDocTimestamp(payload))
        }
      })
    }
    return dbAccessor.updateFieldsStore(payload, 'tasks', 'tasks', 'active', payload.taskId)
  },
  uploadLabels ({commit, getters}, payload) {
    return dbAccessor.uploadFile(payload, 'labels')
  },
  uploadFiles ({commit, getters}, payload) {
    let {newFile, type, newName} = payload
    return dbAccessor.uploadFile(newFile, type, newName)
  },
  removeFile ({commit, getters}, payload) {
    return dbAccessor.removeFile(payload)
  },
  downloadFile ({commit, getters}, payload) {
    return dbAccessor.downloadFile(payload.fullPath, payload.name, payload.isPrint)
  },
  printFile ({commit, getters}, payload) {
    return dbAccessor.printFile(payload.fullPath, payload.name)
  },
  getBalanceForUser ({commit, getters}, payload) {
    // todo: need refactor
    getters.subscribed.balanceForUser && getters.subscribed.balanceForUser()
    let data = 'balanceForUser'
    commit('clearSubscribed', {data, initValues: initialState[data]})
    let balanceKey = `${payload}_${getters.uid}`

    let successFunc = (doc) => {
      let balance = doc.data()
      balance.balanceKey = doc.id
      commit('setBalanceForUser', balance)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['balance', balanceKey])
    commit('addTosubscribed', {balanceForUser: unsubscribe})
  },
  clearBalanceForUserData ({getters, commit}) {
    getters.subscribed.balanceForUser && getters.subscribed.balanceForUser()
    let data = 'balanceForUser'
    commit('clearSubscribed', {data, initValues: initialState[data]})
  },
  resetBalance ({commit, getters}) {
    state.balanceForUser = initialState.balanceForUser
  },
  getAllTenantLimitedInfo ({commit, getters}) {
    return dbAccessor.queryStore('tenantLimitedInfo')
      .then((docs) => {
        let tenantLimitedInfo = {}
        let workFor = getters.activeWorkfor
        docs.forEach((doc) => {
          workFor && workFor[doc.id] && workFor[doc.id] > 0 && (tenantLimitedInfo[doc.id] = doc.data())
        })
        return commit('setAllTenantLimitedInfo', tenantLimitedInfo)
      })
  },
  addPaymentRequest ({commit, getters, dispatch}, payload) {
    let nullfields = getNullFields(payload)
    if (nullfields.length > 0) {
      let error = `payload contains null value property (${nullfields.join(',')}). Add payment canceled`
      return Promise.reject(Error(error))
    }

    payload[`lastRead_${getters.uid}`] = dbAccessor.getServerTimestamp()
    payload.userKey = getters.uid
    payload.userName = getters.userExtra.name
    let {tenantKey, userKey, amount} = payload
    Logger.log('addPaymentRequest', payload)
    return dbAccessor.runInTransactionStore(async transaction => {
      let balanceDoc = await transaction.get(dbAccessor.buildStoreQuery(['balance', `${tenantKey}_${userKey}`]))
      let paymentRef = dbAccessor.buildStoreQuery(['paymentRequest']).doc()
      let {released, total} = balanceDoc.data()
      if (released >= amount && total >= amount) {
        transaction.update(balanceDoc.ref, dbAccessor.addUpdateDocTimestamp({released: released - amount}))
        transaction.set(paymentRef, dbAccessor.addNewDocTimestamp(payload))
      } else {
        throw Error('Negative balance.')
      }
    })
      .catch((error) => {
        Logger.critical('addPaymentRequest', error.message)
      })
  },
  updatePaymentRequest ({commit, getters, dispatch}, payload) {
    let nullfields = getNullFields(payload)
    if (nullfields.length > 0) {
      let error = `payload contains null value property (${nullfields.join(',')}). Add payment canceled`
      return Promise.reject(Error(error))
    }

    let method = payload.method
    Logger.log('updatePaymentRequest', payload)
    return dbAccessor.runInTransactionStore(async transaction => {
      let paymentRequestDoc = await transaction.get(dbAccessor.buildStoreQuery(['paymentRequest', payload.paymentKey]))
      if (!paymentRequestDoc.exists) throw Error('Payment request has been removed or paid')

      transaction.update(paymentRequestDoc.ref, dbAccessor.addUpdateDocTimestamp({method}))
    })
  },
  setPaymentRequestStar ({commit, getters, dispatch}, payload) {
    return dbAccessor.runInTransactionStore(async transaction => {
      let paymentRequestDoc = await transaction.get(dbAccessor.buildStoreQuery(['paymentRequest', payload.paymentKey]))
      if (!paymentRequestDoc.exists) throw Error('Payment request has been removed or paid')
      let starStatus = paymentRequestDoc.data().starStatus
      if (!(starStatus instanceof Array) || starStatus.length !== 2) starStatus = [false, false]
      starStatus[payload.starIndex] = !starStatus[payload.starIndex]
      transaction.update(paymentRequestDoc.ref, { starStatus })
    })
  },
  getPaymentRequestForUser ({commit, getters}) {
    commit('setLoading', true)
    let successFunc = (docs) => {
      let paymentRequests = []
      docs.forEach((doc) => {
        paymentRequests.push({...convertTimestampToDateInObj(doc.data()), paymentKey: doc.id})
      })
      commit('setPaymentRequests', paymentRequests)
      commit('setLoading', false)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let predicates = [{
      field: 'userKey',
      compare: '==',
      value: getters.uid
    }]

    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, predicates, ['paymentRequest'], 'createTime', false)
    commit('addTosubscribed', {paymentRequests: unsubscribe})
  },
  clearPaymentRequestData ({getters, commit}) {
    getters.subscribed.paymentRequests && getters.subscribed.paymentRequests()
    let data = 'paymentRequests'
    commit('clearSubscribed', {data, initValues: initialState[data]})
  },
  addPaymentMethod ({getters, commit}, payload) {
    dbAccessor.queryStore('users', getters.uid)
      .then((doc) => {
        let paymentMethods = doc.data().paymentMethods
        if (payload.index === -1) {
          paymentMethods.push(payload.paymentMethod)
        } else {
          paymentMethods[payload.index] = payload.paymentMethod
        }
        let newPayload = {
          paymentMethods: paymentMethods
        }
        dbAccessor.updateFieldsStore(newPayload, 'users', getters.uid)
      })
  },
  deletePaymentMethod ({getters, commit}, payload) {
    const {displayName} = payload
    dbAccessor.queryStore('users', getters.uid)
      .then((doc) => {
        let paymentMethods = doc.data().paymentMethods
        paymentMethods = paymentMethods.filter(item => item.displayName !== displayName)
        return dbAccessor.updateFieldsStore({paymentMethods}, 'users', getters.uid)
      })
  },
  getPaymentMethod ({getters, commit}) {
    commit('setLoading', true)
    let successFunc = (doc) => {
      let paymentMethods = doc.data().paymentMethods.map(method => {
        method.filter = Object.values(method).join(' ')
        return method
      })
      commit('setPaymentMethods', paymentMethods)
      commit('setLoading', false)
    }
    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }
    let predicates = []
    let unsubscribe = dbAccessor.queryStoreRT(successFunc, errorFunc, predicates, 'users', getters.uid)
    commit('addTosubscribed', {paymentMethods: unsubscribe})
  },
  getPendingTransactionForUser ({commit, getters}) {
    commit('setLoading', true)
    let predicates = [
      {
        field: `userKey`,
        compare: '==',
        value: getters.uid
      },
      {
        field: `isPending`,
        compare: '==',
        value: true
      }
    ]
    return dbAccessor.queryWithPredicates(predicates, ['transaction'], 'pendingEndDate', false)
      .then((docs) => {
        let pendingTransactions = []
        docs.forEach((doc) => {
          pendingTransactions.push(convertTimestampToDateInObj(doc.data()))
        })
        commit('setPendingTransactions', pendingTransactions)
        commit('setLoading', false)
      })
  },
  getHistoryTransactionForUser ({commit, getters}, payload) {
    let predicates = [
      {
        field: `userKey`,
        compare: '==',
        value: getters.uid
      }
    ]

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])

    if (payload && payload.predicates && payload.predicates.predicateCheckBox) {
      predicates.push({
        field: `transactionType`,
        compare: '==',
        value: 'payment'
      })
    }

    return dbAccessor.queryWithPagination(predicates, ['transaction'], 'createTime', true, payload && payload.startAfter, payload && payload.limit)
  },
  cancelUserPaymentRequest ({commit, getters}, payload) {
    Logger.log('cancelUserPaymentRequest', payload)
    let balanceKey = `${payload.tenantKey}_${payload.userKey}`
    return dbAccessor.runInTransactionStore(async transaction => {
      let paymentRequestDoc = await transaction.get(dbAccessor.buildStoreQuery(['paymentRequest', payload.paymentKey]))
      if (!paymentRequestDoc.exists) throw Error('Payment request has been removed or canceled by the user')
      let balanceDoc = await transaction.get(dbAccessor.buildStoreQuery(['balance', balanceKey]))
      if (!balanceDoc.exists) throw Error('balance doc is missing')
      let {released} = balanceDoc.data()
      released = addNumbers(released, payload.amount)

      transaction.delete(paymentRequestDoc.ref)
      transaction.update(balanceDoc.ref, dbAccessor.addUpdateDocTimestamp({released}))
    })
      .then(() => {
        if (balanceKey === getters.balanceForUser.balanceKey) {
          let balance = getters.balanceForUser
          balance.released += payload.amount
          return commit('setBalanceForUser', balance)
        }
      })
  },
  addComment ({getters, commit}, payload) {
    let {comment, docPath, commentsField} = payload
    return dbAccessor.updateFieldAddToArrayStore(commentsField, comment, ...docPath)
  },
  removeComment ({getters, commit}, payload) {
    let {comment, docPath, commentsField} = payload
    comment = convertTimestampToDateInObj(comment)
    let createTime
    if (comment.createTime) {
      createTime = comment.createTime
    } else { return Promise.reject(Error('create time missing')) }
    let predicate = {
      field: 'createTime',
      value: createTime
    }
    return dbAccessor.updateFieldRemoveOneFromArrayStore(commentsField, predicate, ...docPath)
  },
  updateCommentLastRead ({commit, getters}, payload) {
    const {docPath, readTime, commentsField} = payload
    let lastReadUid = (commentsField !== 'comments' && commentsField !== undefined) ?
      `lastRead_${commentsField}_${getters.uid}` :
      `lastRead_${getters.uid}`
    let newPayload = {
      // server time is different from local machine. And array doesn't support server time
      [lastReadUid]: readTime || new Date()
    }
    dbAccessor.updateFieldsStore(newPayload, ...docPath)
  },
  getWarehousesForUser ({getters, commit}) {
    let tenantKeys = Object.keys(getters.activeWorkfor)
    let warehouses = {}
    let promises = tenantKeys.map((key) => {
      return dbAccessor.queryStore('tenantLimitedInfo', key)
        .then(doc => {
          let warehousesIn = doc.data().warehouses
          warehouses = {...warehouses, ...warehousesIn}
        })
    })
    return Promise.all(promises)
      .then(() => {
        let warehousesFin = Object.values(warehouses)
        warehousesFin.forEach((warehouseFin, index) => {
          warehouseFin.warehouseId = warehouseFin.warehouseKey
          warehouseFin.warehouseKey = Object.keys(warehouses)[index]
        })
        commit('setWarehousesForUser', warehousesFin)
      })
  },
  getUserInventory ({getters, commit}, payload) {
    let predicates = [
      {
        field: `tenantKey`,
        compare: '==',
        value: payload
      }
    ]
    return dbAccessor.queryWithPredicatesStore(predicates, 'userLimitedInfo', getters.uid, 'inventory')
  },
  getAllRegistrationCode ({getters, commit}) {
    return dbAccessor.queryWithPredicates([], ['sysAdmin', 'general', 'codes'], 'createTime', false)
  },
  addRegistrationCode ({getters, commit}, {type = 'tenant'}) {
    let newCode = dbAccessor.getNewDocumentKey('sysAdmin', 'general', 'codes').id
    let payload = {code: newCode, type}
    return dbAccessor.insertStore(payload, 'sysAdmin', 'general', 'codes')
      .then(() => {
        payload.createTime = new Date()
        return payload
      })
  },
  getReportLostUser ({commit, getters}) {
    let predicates = [{
      field: `userKey`,
      compare: '==',
      value: getters.userExtra.uid
    }]

    let successFunc = (docs) => {
      let reportLost = docs.docs.map(doc => {
        let reportDoc = convertTimestampToDateInObj(doc.data())
        return {reportKey: doc.id, ...reportDoc}
      })
      commit('setReportLost', reportLost)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, predicates, ['reportLost'], 'createTime', false)
    commit('addTosubscribed', {reportLost: unsubscribe})
  },
  addLostReport ({getters, commit, dispatch}, payload) {
    Logger.log('addLostReport', payload)
    payload.userKey = getters.userExtra.uid
    payload.userName = getters.userExtra.name
    let userRef = dbAccessor.buildStoreQuery(['userLimitedInfo', payload.userKey, 'inventory', payload.productKey])
    let addReportRef = dbAccessor.buildStoreQuery(['reportLost']).doc()
    let productRef = dbAccessor.buildStoreQuery(['tenants', payload.tenantKey, 'inventory', payload.productId])

    return dbAccessor.runInTransactionStore(transaction => {
      return Promise.all([transaction.get(userRef), transaction.get(productRef)])
        .then(docArray => {
          let {condition, name} = docArray[1].data()
          let price = docArray[0].data().price || 0
          payload = {...payload, condition, price, name}
          payload.upc = docArray[1].data().upc
          docArray[1].data().asin && (payload.asin = docArray[1].data().asin)
          let newPayload = {}
          let distribution = docArray[0].data().distribution
          newPayload.quantity = docArray[0].data().quantity - payload.quantity
          if (distribution[payload.distributionKey].quantity <= payload.quantity) {
            delete distribution[payload.distributionKey]
          } else {
            distribution[payload.distributionKey].quantity -= payload.quantity
          }

          newPayload.distribution = distribution
          transaction.update(userRef, dbAccessor.addUpdateDocTimestamp(newPayload))
          transaction.set(addReportRef, dbAccessor.addNewDocTimestamp(payload))
          transaction.update(productRef, dbAccessor.addUpdateDocTimestamp({}))
        })
    })
  },
  getTenantLimitedInfoForUser ({getters, commit}, payload) {
    return dbAccessor.queryStore('tenantLimitedInfo', payload)
  },
  async confirmShipment ({commit, getters}, payload) {
    Logger.log('confirmShipment', payload)
    let userType
    if (getters.activeOrganization) {
      userType = 'tenant'
    } else if (getters.activeWarehouse) {
      userType = 'warehouse'
    } else {
      userType = 'user'
    }
    const operator = getters.operator
    const workerKey = operator.uid
    const workerName = operator.name
    payload.draftComments.forEach(comment => {
      comment.createTime = comment.createTime.toISOString ? comment.createTime.toISOString() : comment.createTime
    })
    return dbAccessor.callFunction('confirmShipment', {payload: {...payload, createTime: payload.createTime.toISOString()}, userType, workerKey, workerName})
      .catch((error) => {
        const msg = error.message
        switch (msg) {
          case 'shipment-missing':
            throw Error('Shipment missing.')
          case 'quantity-error':
            if (userType === 'user') {
              if (getters.reportLost?.length) throw Error('checkReportLost')
              else throw Error('contactSupport')
            }
            throw Error('Quantity error.')
          case 'user inventory missing':
          case 'inventory-missing':
            throw Error('Inventory missing.')
          case 'distribution-quantity-error':
            throw Error('insufficient quantity')
          default:
            throw error
        }
      })
  },
  async getLabelInfoInEeveeWithAxios ({commit, getters, dispatch}, payload) {
    const allTrackingNumberInfo = []
    const {trackingNumbers = []} = payload
    const MAX_REQUEST = 30
    const requestRounds = Math.ceil(trackingNumbers.length / MAX_REQUEST)
    for (let index = 0; index < requestRounds; index++) {
      const startIndex = index * MAX_REQUEST
      const endIndex = (1 + index) * MAX_REQUEST <= trackingNumbers.length ? (1 + index) * MAX_REQUEST : trackingNumbers.length
      const dataPieces = trackingNumbers.slice(startIndex, endIndex)
      const TrackingNumberPiecesInfo = await dispatch('getLabelInfoInEevee', {trackingNumbers: dataPieces})
      TrackingNumberPiecesInfo.data.length && allTrackingNumberInfo.push(...TrackingNumberPiecesInfo.data)
    }
    return allTrackingNumberInfo
  },
  async saveReturnPackages ({commit, getters, dispatch}, payload = []) {
    const newAddedTrackingNumberList = []
    const systemEeveeOrgId = getters.eeveeOrgId
    const eeveeIdMatchTenantKeyMap = getters.eeveeIdMatchTenantKeyMap
    for (let index = 0; index < payload.length; index++) {
      const {isExists, returnCreateTime, comments = [], orgId = '', trackingNumber, ...rest} = payload[index]
      try {
        const needToSaveDataObj = {...rest, comments, trackingNumber, returnCreateTime, orgId}
        if (systemEeveeOrgId !== orgId) {
          needToSaveDataObj.clientKey = eeveeIdMatchTenantKeyMap.get(orgId) || orgId
        }
        await dispatch('updateSingleReturnPackage', needToSaveDataObj)
        newAddedTrackingNumberList.push(trackingNumber)
      } catch (error) {
        console.log('error', error)
      }
    }
    await dispatch('addNewTrackingsToCache', newAddedTrackingNumberList)
  },
  async addNewTrackingsToCache ({commit, getters, dispatch}, newAddedTrackingNumberList) {
    const maximumCacheLength = 5000
    const query = dbAccessor.buildStoreQuery(['sysAdmin', 'returnPackageConfig'])
    const targetDoc = await query.get()
    if (!targetDoc.exists) {
      query.set({returnPackageCache: newAddedTrackingNumberList})
    } else {
      const {returnPackageCache = []} = targetDoc.data()
      const needToSaveTrackingNumberCache = [...returnPackageCache, ...newAddedTrackingNumberList]
      return dbAccessor.updateFieldsStore({returnPackageCache: needToSaveTrackingNumberCache.length <= maximumCacheLength ? needToSaveTrackingNumberCache : needToSaveTrackingNumberCache.slice(needToSaveTrackingNumberCache.length - maximumCacheLength)}, 'sysAdmin', 'returnPackageConfig')
    }
  },
  subscribeReturnPkgsCache ({getters, commit}, payload) {
    const cbFunc = (data) => {
      const { returnPackageCache = [] } = data
      commit('setReturnPkgCache', returnPackageCache)
    }
    return dbAccessor.queryStoreRT(
      (doc) => (doc.exists && cbFunc(convertTimestampToDateInObj(doc.data()))) || {},
      (err) => { console.error(err) },
      [], 'sysAdmin', 'returnPackageConfig'
    )
  },
  async updateSingleReturnPackage ({commit, getters, dispatch}, payload) {
    const {trackingNumber = '', clientKey, clientName, orgId} = payload
    const theReturnPkgDoc = await dbAccessor.queryStore('returnPackages', trackingNumber)
    if (!theReturnPkgDoc.exists) {
      dbAccessor.updateStore({...payload, keywords: formatKeywords([trackingNumber, clientKey, clientName, orgId])}, 'returnPackages', trackingNumber)
    }
  },
  async deleteSingleReturnPackage ({commit, getters, dispatch}, payload) {
    const { id } = payload
    return dbAccessor.runInBatch(batch => {
      batch.delete(dbAccessor.buildStoreQuery(['returnPackages', id]))
      batch.update(dbAccessor.buildStoreQuery(['sysAdmin', 'returnPackageConfig']), {returnPackageCache: dbAccessor.fieldArrayRemove([id])})
    })
  },
  async updateReturnPackage ({commit, getters, dispatch}, payload) {
    const {trackingNumber = '', status = '', originStatus, note = '', comments = [], readyLabels = [], ...rest} = payload
    const targetPackageDoc = await dbAccessor.buildStoreQuery(['returnPackages', trackingNumber]).get()
    if (!targetPackageDoc.exists) throw Error('Package not found')
    const targetDocData = targetPackageDoc.data()
    if (targetDocData.status !== originStatus) throw Error('Status already changed, please refresh')
    const needToUpdateData = {status, ...rest}
    readyLabels.length && (needToUpdateData.readyLabels = readyLabels)
    comments.length && (needToUpdateData.comments = dbAccessor.fieldArrayUnion(comments))
    note && (needToUpdateData.note = note)
    return dbAccessor.updateFieldsStore(needToUpdateData, 'returnPackages', trackingNumber)
  },
  getLabelInfoInEevee ({commit, getters, dispatch}, {trackingNumbers = []}) {
    const url = `${process.env.eeveeApi.url}admin/client/label`
    return axios.post(url, {trackingNumbers})
  },
  updateAnnouncement ({commit, getters}, payload) {
    let announcement = new Announcement(payload)
    return announcement.update()
  },
  addAnnouncement ({commit, getters}, payload) {
    let broadcasterType = ''
    let broadcasterName = ''
    let broadcasterKey = ''
    if (getters.activeOrganization) {
      broadcasterType = Announcement.tenantCode
      broadcasterName = getters.tenant.name
      broadcasterKey = getters.activeOrganization
    } else if (getters.activeWarehouse) {
      broadcasterType = Announcement.warehouseCode
      broadcasterName = getters.warehouseName
      broadcasterKey = getters.activeWarehouse
    } else if ((getters.user || {}).isAdmin) {
      broadcasterType = Announcement.systemCode
      broadcasterName = 'System'
      broadcasterKey = 'sys'
    }
    if (!broadcasterType) {
      throw Error('Error announcement sender')
    }
    let announcement = new Announcement({
      ...payload,
      broadcasterKey,
      broadcasterType,
      broadcasterName
    })
    return announcement.insert()
  },
  deleteAnnouncement ({commit, getters}, payload) {
    return (payload instanceof Announcement) ? payload.delete() : Announcement.deleteByKey(payload._key)
  },
  async getAllAnnouncements ({commit, getters}) {
    let generalCbFunc = (key) => {
      let cbFunc = (data) => {
        let today = new Date()
        let newAnnouncementsMap = new Map([...getters.announcementsMap])
        const announcements = data.filter((item) => {
          const {startDate, clients = []} = item
          const isDateValid = startDate <= today
          const isOrg = getters.activeOrganization
          const isHasClients = !!clients.length
          if (!isHasClients) {
            return isDateValid
          }
          const hasSelectedClient = isOrg && clients.some(client => client.key === getters.activeOrganization)
          return isDateValid && hasSelectedClient
        })
          .sort((announcement1, announcement2) => parseInt(announcement2.startDate - announcement1.startDate))
        newAnnouncementsMap.set(key, announcements)
        commit('setAnnouncementsMap', newAnnouncementsMap)
      }
      return cbFunc
    }

    let {activeWorkfor = {}, activeOrganization, warehouses = [], activeWarehouse} = getters
    let sysAnnouncementsSubscribe =
      Announcement.getSysAnnouncementsRT(Announcement.getUserTypeCode(activeOrganization, activeWarehouse), generalCbFunc('sys'))
    commit('addTosubscribed', {sysAnnouncementsReciever: sysAnnouncementsSubscribe})
    // assert warehouse user
    if (activeWarehouse) return
    // assert tenant user
    if (activeOrganization) {
      warehouses
        .filter(({warehouseKey}) => warehouseKey !== '' && warehouseKey !== activeOrganization)
        .forEach(({warehouseKey}) => {
          let unsubscribe = Announcement.getWarehouseAnnouncementsRT(warehouseKey, generalCbFunc(warehouseKey))
          commit('addTosubscribed', {[`warehouseAnnouncement_${warehouseKey}`]: unsubscribe})
        })
      return
    }
    // individual collect org!
    Object.keys(activeWorkfor)
      .forEach((tenantKey) => {
        let unsubscribe = Announcement.getTenantAnnouncementsRT(tenantKey, generalCbFunc(tenantKey))
        commit('addTosubscribed', {[`tenantAnnouncement_${tenantKey}`]: unsubscribe})
      })
  },
  updateUserExtraReadList ({commit, getters}, payload) {
    if (!getters.userExtra) return
    let storeReadList = getters.userExtra.announcementsReadList || []
    if (
      payload.length === storeReadList.length &&
      !(payload.filter(key => !(storeReadList.includes(key))).length)
    ) return
    return dbAccessor.updateFieldsStore({
      announcementsReadList: payload
    }, 'users', getters.userExtra.uid)
  },
  getAdminAnnouncements ({commit, getters}, payload) {
    const cbFunc = (data) => {
      commit('setAdminAnnouncements', data)
    }
    let unsubscribe = Announcement.getByAdminRT(cbFunc)
    commit('addTosubscribed', {tenantAnnouncements: unsubscribe})
  },
  sendNewCommentsByEmail ({commit, getters}, payload) {
    return dbAccessor.callFunction('sendNewCommentsByEmail', payload)
  },
  addNotification ({commit, getters}, payload) {
    if (!payload.key) return

    commit('removeNotification', payload)
    commit('addNotification', payload)
  },
  /** @param {{fullPath: string}} payload */
  getStorageFileUrl ({getters}, payload) {
    return dbAccessor.getFileUrl(payload.fullPath)
  },
  /** @param {{predicates: Predicate[]}} payload */
  async searchPackagesInAllWarehouse ({getters}, payload) {
    const {predicates} = payload
    if (!Array.isArray(predicates) || predicates.length === 0) return []
    const querySnapshots = await dbAccessor.queryCollectionGroup(
      predicates,
      'packages'
    )
    return querySnapshots.size ?
      querySnapshots.docs.map(doc => {
        const warehouseKey = doc.ref.path.split('/')[1]
        return new Package(Package._extractDoc(doc), warehouseKey, getters.uid)
      }) :
      []
  },
  getFavoritePackages ({getters}, payload) {
    let predicates = [
      {field: 'favorites', compare: 'array-contains', value: getters.uid}
    ]

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])

    return dbAccessor.queryCollectionGroupWithPagination(predicates, 'packages', 'createTime', true, payload && payload.startAfter, payload && payload.limit)
      .then(querySnapshots => querySnapshots.size ?
        {models: querySnapshots.docs.map(doc => {
          const warehouseKey = doc.ref.path.split('/')[1]
          return new Package(Package._extractDoc(doc), warehouseKey, getters.uid)
        }),
        docs: querySnapshots} : {models: [], docs: querySnapshots})
  },
  /**
   * @param {{docPath: string[], cbFunc: function}} payload
   * @returns {function}
   */
  subscribeComments ({getters}, payload) {
    const {cbFunc, docPath = []} = payload
    if (typeof cbFunc !== 'function') throw Error('cbFunc-must-be-function')
    return dbAccessor.queryStoreRT(
      (doc) => (doc.exists && cbFunc(convertTimestampToDateInObj(doc.data()))) || {},
      (err) => { console.error(err) },
      [], ...docPath
    )
  },
  /**
   *
   * @param {{
   *  isFavorite: boolean,
   *  warehouseKey: string,
   *  packageKey: string,
   *  uid: string
   * }} payload
   * @returns {boolean}
   */
  setPackageFavorite ({getters}, payload) {
    return (payload.isFavorite ?
      dbAccessor.updateFieldAddToSetArray(
        ['warehouses', payload.warehouseKey, 'packages', payload.packageKey],
        'favorites', payload.uid
      ) :
      dbAccessor.updateFieldRemoveFromSetArray(
        'favorites', payload.uid,
        ['warehouses', payload.warehouseKey, 'packages', payload.packageKey]
      ))
      .then(() => payload.isFavorite)
  },
  async addUserToAdmin ({commit, getters, dispatch}, payload) {
    const {email, roles} = payload
    let predicates = [{
      field: 'email',
      compare: '==',
      value: email
    }]
    let docs = await dbAccessor.queryWithPredicatesStore(predicates, 'users')

    if (docs.size === 0) {
      throw Error('Email address not found.')
    }
    const userDoc = docs.docs[0]
    const { name, warehouses = [], organizations = [], accounts = [], role } = userDoc.data()
    if (role !== 1) throw Error('Only individuals can be staff of the company.')
    if (accounts.length >= 10) {
      throw Error('He became an staff(member) of up to ten companies, cannot add.')
    }
    let userPayload = {
      accounts: dbAccessor.fieldValue().arrayUnion({
        email,
        name,
        key: userDoc.id,
        isAdmin: true,
        roles
      })
    }
    if (warehouses.length && !accounts.length) {
      const {users = [], name: warehousesName} = (await dbAccessor.queryStore('warehouses', warehouses[0])).data()
      let warehousesInfo = users.find(user => user.name === warehousesName)
      userPayload = {
        accounts: [
          {
            email: warehousesInfo.email,
            name: warehousesInfo.name,
            key: warehouses[0],
            role: 2,
            roles: warehousesInfo.roles
          },
          {
            email,
            name,
            key: userDoc.id,
            isAdmin: true,
            roles
          }]
      }
    }
    if (organizations.length && !accounts.length) {
      const {users = [], name: tenantName} = (await dbAccessor.queryStore('tenants', organizations[0])).data()
      let tenantInfo = users.find(user => user.name === tenantName)
      userPayload = {
        accounts: [
          {
            email: tenantInfo.email,
            name: tenantInfo.name,
            key: organizations[0],
            role: 0,
            roles: tenantInfo.roles
          },
          {
            email,
            name,
            key: userDoc.id,
            isAdmin: true,
            roles
          }]
      }
    }
    let params = userPayload.accounts.reduce?.((acc, account) => {
      acc[account.key] = account.roles
      return acc
    }, {
      email: payload.email,
      roles: accounts[0]?.roles || payload.roles,
      [userDoc.id]: payload.roles
    }) ||
    accounts.reduce((acc, account) => {
      acc[account.key] = account.roles
      return acc
    }, {
      email: payload.email,
      roles: accounts[0]?.roles || payload.roles,
      [userDoc.id]: payload.roles
    })
    return dbAccessor.runInTransactionStore(async transaction => {
      await dbAccessor.callFunction('grantUserRole', params)
      transaction.set(dbAccessor.buildStoreQuery(['sysAdmin', 'general', 'superusers', userDoc.id]), dbAccessor.addNewDocTimestamp({
        name,
        email,
        roles,
        note: payload.note || ''
      }))
      transaction.update(userDoc.ref, userPayload)
    })
  },
  async removeUserFromAdmin ({commit, getters, dispatch}, payload) {
    let superuserRef = dbAccessor.buildStoreQuery(['sysAdmin', 'general', 'superusers', payload.key])
    const predicates = [
      {
        field: `email`,
        compare: '==',
        value: payload.email
      }
    ]
    const userDocs = await dbAccessor.queryWithPredicatesStore(predicates, 'users')
    if (userDocs.size === 0) {
      throw Error('Email address not found.')
    }
    const userRef = userDocs.docs[0].ref
    let params = {}
    return dbAccessor.runInTransactionStore(async (transaction) => {
      return Promise.all([transaction.get(superuserRef), transaction.get(userRef)])
        .then(async ([superuserDoc, userDoc]) => {
          let { accounts = [] } = userDoc.data()
          let { email } = superuserDoc.data()
          let userAccounts = accounts.filter((account) => account.email !== email)
          let userPayload = {
            accounts: userAccounts
          }
          params = userAccounts.reduce((acc, account) => {
            acc[account.key] = account.roles
            return acc
          }, {
            email: payload.email,
            roles: ['user']
          })
          if (accounts[0].email !== email) params.roles = accounts[0].roles
          if (accounts[1] && accounts[0].email === email) {
            params.roles = accounts[1].roles
            if (accounts[1].role === 0) userPayload.organizations = [accounts[1].key]
            if (accounts[1].role === 2) userPayload.warehouses = [accounts[1].key]
          }
          await dbAccessor.callFunction('grantUserRole', params)
          transaction.delete(superuserRef)
          transaction.update(userRef, userPayload)
        })
    })
  },
  async editUserForAdmin  ({commit, getters, dispatch}, payload) {
    // is there security risk?
    const {email, key, roles} = payload
    let adminRef = await dbAccessor.buildStoreQuery(['sysAdmin', 'general', 'superusers', key])
    let predicates = [{
      field: 'email',
      compare: '==',
      value: email
    }]
    let userDocs = await dbAccessor.queryWithPredicatesStore(predicates, 'users')
    if (userDocs.size === 0) {
      throw Error('Email address not found.')
    }
    let params = {email}
    return dbAccessor.runInTransactionStore(async (transaction) => {
      return Promise.all([transaction.get(adminRef), transaction.get(userDocs.docs[0].ref)])
        .then(async ([adminDoc, userSnapshot]) => {
          const userDoc = userDocs.docs[0]
          let { accounts = [] } = userDoc.data()
          const { name } = adminDoc.data()
          if (!accounts.length) {
            accounts = [{
              email: email,
              name: name,
              key: userDoc.id,
              isAdmin: true,
              roles: payload.roles
            }]
          }
          accounts.forEach(account => {
            if (account.email === email) {
              account.roles = roles
              params[account.key] = roles
            } else params[account.key] = account.roles
          })
          params.roles = accounts[0].roles
          await dbAccessor.callFunction('grantUserRole', params)
          transaction.update(adminRef, {roles, note: payload.note || ''})
          transaction.update(userSnapshot.ref, {accounts})
        })
    })
  },
  getSuperUsers ({commit, getters}) {
    const successFunc = (docs) => {
      const users = docs.docs.map(doc => {
        const obj = convertTimestampToDateInObj(doc.data())
        return {...obj, key: doc.id}
      })
      commit('setSuperUsers', users)
    }

    const errorFunc = (error) => {
      console.log(error)
      throw error
    }
    const unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['sysAdmin', 'general', 'superusers'], 'createTime', false)
    commit('addTosubscribed', {superUsers: unsubscribe})
  },
  async unsubscribleOrg ({commit, getters}, {tenantKey, tenantName}) {
    const {uid: userKey, name: userName, email} = getters.userExtra
    const balanceDoc = await dbAccessor.queryStore('balance', `${tenantKey}_${userKey}`)
    const {total = 0} = balanceDoc.data() || {}
    const isBalanceClean = total === 0
    const isInventoryClean = getters.userInventory
      .filter(inventory => inventory.tenantKey === tenantKey)
      .every(item => !item.quantity)
    const isProposeClean = !getters.proposes
      .filter(item => item.tenantKey === tenantKey)
      .length
    const isReportLostClean = !getters.reportLost
      .filter(item => item.tenantKey === tenantKey)
      .length
    const isTasksClean = !getters.tasks.length

    if (!isBalanceClean) throw Error('Balance not clean.')
    if (!isInventoryClean) throw Error('Inventory not clean.')
    if (!isProposeClean) throw Error('Propose not clean.')
    if (!isReportLostClean) throw Error('ReportLost not clean.')
    if (!isTasksClean) throw Error('task not clean.')

    await dbAccessor.callFunction('unsubscribeOrg', {tenantKey, userKey})
    await Logger.changeLog({
      beforeValue: {},
      actionPayload: {
        tenantKey,
        userKey,
        email,
        userName,
        tenantName
      },
      actionName: 'unsubscribleOrg',
      categories: ['userInfo'],
      keys: [tenantKey, userKey],
      keywords: [
        tenantKey,
        userKey,
        email,
        userName,
        tenantName
      ]
    })
  },
  async unsubscribeUser ({commit, getters}, payload) {
    const { email: operatorEmail, name: tenantName } = getters.operator
    const { uid: userKey, name: userName, email } = payload
    const tenantKey = getters.activeOrganization
    await dbAccessor.callFunction('unsubscribeUser', {
      userKey,
      userName,
      tenantKey,
      tenantName,
      operatorEmail,
      email
    })
    await Logger.changeLog({
      beforeValue: {},
      actionPayload: {
        tenantKey,
        userKey,
        email,
        operatorEmail,
        userName,
        tenantName
      },
      actionName: 'unsubscribeUser',
      categories: ['userInfo'],
      keys: [tenantKey, userKey],
      keywords: [
        tenantKey,
        userKey,
        email,
        operatorEmail,
        userName,
        tenantName
      ]
    })
  },
  async deleteTargetUser (state, payload) {
    return dbAccessor.callFunction('deleteTargetUser', payload)
  },
  buildAllTenantLimitedInfoRT ({commit, getters, dispatch}) {
    if (Object.keys(getters.allTenantLimitedInfo).length && getters.subscribed['tenantLimitedInfo']) return

    let successFunc = (docs) => {
      const allTenantLimitedInfo = []
      docs.forEach(doc => {
        const { name, email, betaFeatures = [], inVisibleFeatures = [], createTime } = doc.data()
        const obj = {
          name,
          email,
          type: 'organization',
          key: doc.id,
          betaFeatures,
          inVisibleFeatures,
          createTime: createTime?.toDate() || ''
        }
        allTenantLimitedInfo.push(obj)
      })
      commit('setAllTenantLimitedInfo', allTenantLimitedInfo)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['tenantLimitedInfo'])
    commit('addTosubscribed', {tenantLimitedInfo: unsubscribe})
  },
  buildAllWarehouseLimitedInfoRT ({commit, getters, dispatch}) {
    if (Object.keys(getters.allWarehouseLimitedInfo).length && getters.subscribed['warehouseLimitedInfo']) return

    let successFunc = (docs) => {
      const allWarehouseLimitedInfo = []
      docs.forEach(doc => {
        const { warehouseName, email, betaFeatures = [], isNeedDepositFee = false, createTime, isRestricted, inVisibleFeatures = [] } = doc.data()
        const obj = {
          name: warehouseName,
          email,
          type: 'warehouse',
          key: doc.id,
          betaFeatures,
          isNeedDepositFee,
          createTime: createTime?.toDate() || '',
          isRestricted,
          inVisibleFeatures
        }
        allWarehouseLimitedInfo.push(obj)
      })
      commit('setAllWarehouseLimitedInfo', allWarehouseLimitedInfo)
    }

    let errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    let unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['warehouseLimitedInfo'])
    commit('addTosubscribed', {warehouseLimitedInfo: unsubscribe})
  },
  buildAllLimitedInfoRT ({commit, getters, dispatch}) {
    dispatch('buildAllTenantLimitedInfoRT')
    dispatch('buildAllWarehouseLimitedInfoRT')
  },
  async loadStatistics ({ commit }) {
    const docs = await dbAccessor.queryStore('statistics')
    if (docs.empty) return
    const result = {
      labelStatistics: { Service: LabelStatistics, data: {} },
      dailyAbnormalStatistics: { Service: AbnormalStatistics, data: {} }
    }
    docs.docs.forEach(doc => {
      const data = doc.data()
      const { clientName, clientKey } = data
      Object.keys(result).forEach(key => {
        const value = data[key] || []
        value.forEach(({ date, statPeriod, ...rest }) => {
          const statDate = date || statPeriod
          const { data, Service } = result[key]
          const dateStatistics = data[statDate] || []
          dateStatistics.push(new Service({ ...rest, clientName, clientKey }))
          data[statDate] = dateStatistics
        })
      })
    })
    commit('setLabelStatistics', result.labelStatistics.data)
    commit('setAbnormalStatistics', result.dailyAbnormalStatistics.data)
  },
  async getDailyStatistics ({ commit }, payload) {
    const { startDate, endDate } = payload
    const predicates = [
      {
        field: `dateTime`,
        compare: '>=',
        value: startDate
      },
      {
        field: `dateTime`,
        compare: '<=',
        value: endDate
      }
    ]
    const docs = await dbAccessor.queryWithPredicates(predicates, ['dailyStatistics'], 'dateTime', false)

    if (docs.empty) return []
    const data = docs.docs.map(doc => doc.data())
    const result = Object.values(data.reduce((res, item) => {
      const { clientKey, details } = item
      if (!res[clientKey]) res[clientKey] = { ...item, details }
      else res[clientKey].details.push(...details)
      return res
    }, {}))
    return result
  },
  getFeatureApplication ({commit, getters}, payload) {
    const predicates = [
      {
        field: 'clientKey',
        compare: '==',
        value: getters.activeOrganization
      }
    ]
    return dbAccessor.queryWithPredicates(predicates, ['featureApplication'], 'createTime', false).then((docs) => {
      let applications = []
      docs.forEach((doc) => {
        const obj = doc.data()
        obj._key = doc.id
        applications.push(convertTimestampToDateInObj(obj))
      })
      let application = applications.find(application => application.status === 'pending') || {}
      commit('setFeatureApplication', application)
    })
  },
  async getApplicationList ({commit, getters}, payload) {
    const {startAfter, limit} = payload
    let predicates = []
    if (payload && payload.predicates && payload.predicates.actionPredicates) {
      predicates = [...predicates, ...payload.predicates.actionPredicates]
    }
    return dbAccessor.queryWithPagination(predicates, ['featureApplication'], 'createTime', true, startAfter, limit)
  },
  submitApplication ({commit, getters}, payload) {
    const {feature, link} = payload
    const application = {
      status: 'pending',
      feature,
      link
    }
    return dbAccessor.insertStore(payload, 'featureApplication')
      .then(res => {
        let tenantLimitedInfo = getters.tenantLimitedInfo
        dbAccessor.updateFieldsStore({...tenantLimitedInfo, application}, 'tenantLimitedInfo', getters.activeOrganization)
      })
  },
  async handleApplication ({commit, getters}, payload) {
    const {clientKey, actionType, feature, _key, link} = payload
    let application = {
      status: actionType,
      feature: feature,
      link: link
    }
    await dbAccessor.runInTransactionStore(async transaction => {
      let limitedInfoDoc = await transaction.get(dbAccessor.buildStoreQuery(['tenantLimitedInfo', clientKey]))
      let applicationDoc = await transaction.get(dbAccessor.buildStoreQuery(['featureApplication', _key]))
      const limitedInfo = limitedInfoDoc.data()

      if (actionType === 'reject') {
        transaction.delete(applicationDoc.ref)
      } else {
        limitedInfo.betaFeatures.push(feature)
        transaction.set(applicationDoc.ref, { ...applicationDoc.data(), status: actionType })
      }
      transaction.set(limitedInfoDoc.ref, { ...limitedInfo, application: application })
    })
  },
  async loadSysAdminDepartmentsRT ({ commit, getters }) {
    if (!getters.user?.isAdmin) return

    const successFunc = doc => {
      const departments = doc.get('departments') || []
      commit('setDepartments', departments)
    }

    const errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    const unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['sysAdmin', 'departments'])
    commit('addTosubscribed', {sysAdminDepartment: unsubscribe})
  },
  async loadSysAdminCommonCommoditiesRT ({ commit, getters }) {
    const successFunc = doc => {
      const commonCommodities = doc.get('commonCommodities') || []
      commit('setCommonCommodities', commonCommodities)
    }

    const errorFunc = (error) => {
      console.log(error)
      commit('setLoading', false)
      throw error
    }

    const unsubscribe = dbAccessor.queryRT(successFunc, errorFunc, [], ['sysAdmin', 'commonCommodities'])
    commit('addTosubscribed', {sysAdminDepartment: unsubscribe})
  },
  updateSysAdminDepartmentList ({ getters }, payload) {
    if (!getters.user?.isAdmin) return
    const { departments = [] } = payload
    return dbAccessor.updateStore({ departments }, 'sysAdmin', 'departments')
  },
  updateSysAdminCommonCommoditiesList ({ getters }, payload) {
    if (!getters.user?.isAdmin) return
    const { commonCommodities = [] } = payload
    return dbAccessor.updateStore({ commonCommodities }, 'sysAdmin', 'commonCommodities')
  },
  getTrackingOTP ({commit, getters}, payload) {
    return dbAccessor.queryWithPredicates(payload.predicates, ['trackingOTP'])
  },
  addTrackingOTP ({commit, getters}, payload) {
    const { tracking } = payload
    return dbAccessor.updateStore(payload, 'trackingOTP', tracking)
  },
  async uploadTrackingOTP ({commit, getters}, payload) {
    const { rawData } = payload
    let batches = []

    while (rawData.length > 0) {
      batches = (rawData.splice(0, 400))
      await dbAccessor.runInBatch(batch => {
        batches.forEach(otpInfo => {
          const { tracking } = otpInfo
          const ref = dbAccessor.buildStoreQuery(['trackingOTP', tracking])
          batch.set(ref, dbAccessor.addNewDocTimestamp(otpInfo))
        })
      })
    }
  },
  async forwardAbnormalOtp ({commit, getters, dispatch}, payload) {
    const {content, abnormalOtp} = payload
    const {orgKey, uid} = abnormalOtp
    const dbPromises = []

    if (orgKey) {
      const abnormalOtpOrgRef = dbAccessor.queryStore('tenants', orgKey)
      dbPromises.push(abnormalOtpOrgRef)
    }

    if (uid) {
      const abnormalOtpUserRef = dbAccessor.queryStore('users', uid)
      dbPromises.push(abnormalOtpUserRef)
    }
    const [abnormalOtpOrgRef, abnormalOtpUserRef] = await Promise.all(dbPromises)
    let userEmail = abnormalOtpUserRef?.get('email') || getters.warehouseLimitedInfo.email
    let orgEmail = abnormalOtpOrgRef?.get('email') || ''
    let sendEmail = dispatch('sendMail', {
      subject: 'OTP ERROR',
      recievers: [userEmail, orgEmail],
      body: {
        template: content
      }
    })
    let updateOtp = dispatch('addTrackingOTP', abnormalOtp)
    return Promise.all([sendEmail, updateOtp])
  },
  async uploadLabelsTask ({commit, getters, dispatch}, labels) {
    const { activeOrganization: clientKey, tenantLimitedInfo } = getters

    const body = {
      labels: labels,
      clientKey: clientKey,
      clientName: tenantLimitedInfo.name,
      type: 'uploadLabels'
    }
    return dbAccessor.callFunction('processLabelTask', body)
  },
  getLabelsTaskPagination ({commit, getters}, payload) {
    let predicates = getters.user.isAdmin ? [] : [{
      field: 'clientKey',
      compare: '==',
      value: getters.activeOrganization
    }]

    if (payload.predicates.predicateSelect) {
      predicates.push({
        field: `clientKey`,
        compare: '==',
        value: payload.predicates.predicateSelect
      })
    }

    if (payload.predicates && payload.predicates.predicateText) {
      predicates.push({
        field: `keywords`,
        compare: 'array-contains',
        value: payload.predicates.predicateText.toLowerCase()
      })
    }

    payload.startDate && payload.endDate && (predicates = [...predicates, {
      field: `createTime`,
      compare: '>=',
      value: payload.startDate
    },
    {
      field: `createTime`,
      compare: '<=',
      value: payload.endDate
    }])
    return dbAccessor.queryWithPagination(predicates, ['labelsTask'], 'createTime', true, payload && payload.startAfter, payload && payload.limit)
  },
  deleteLabelsTask ({commit, getters}, payload) {
    const { keys } = payload
    if (keys.length > 25) throw Error('No more than 25 items can be deleted')
    return dbAccessor.callFunction('processLabelTask', {
      keys,
      type: 'deleteShippingLabels',
      clientKey: getters.activeOrganization,
      clientName: getters.tenantLimitedInfo.name
    })
  },
  updateLabelsTaskRate ({commit, getters}, payload) {
    return dbAccessor.runInTransactionStore(async transaction => {
      const labelTaskDoc = await transaction.get(dbAccessor.buildStoreQuery(['labelsTask', payload.key]))
      if (!labelTaskDoc.exists) throw Error('label task has been removed or canceled by the user')
      const {
        shippingService,
        shippingServiceDesc,
        channel,
        serviceType,
        totalAmount
      } = payload.rate
      const updatePayload = {
        shippingService,
        shippingServiceDesc,
        channel,
        serviceType,
        totalAmount
      }
      transaction.update(labelTaskDoc.ref, updatePayload)
    })
  },
  confirmLabelsTask ({ commit, getters }, payload) {
    if ((getters.systemBalance.balance || 0) <= (getters.systemBalance.lowestBalance || 0)) {
      if (!getters.isModeLabel) {
        throw Error('Can not create label, balance is too low.')
      }
    }
    const { keys } = payload
    return dbAccessor.callFunction('processLabelTask', {
      taskKeys: keys,
      type: 'confirmShippingLabels',
      clientKey: getters.activeOrganization,
      clientName: getters.tenantLimitedInfo.name,
      isBeta: getters.isLabelBetaUser,
      margin: getters.systemBalance.margin || 0
    })
  },
  exportClientsRechargeTransaction ({ commit, getters }, payload) {
    const { clients, date } = payload
    const year = date.getFullYear()
    const month = date.getMonth()
    let predicates = [{
      field: 'subType',
      compare: '==',
      value: 'AliPay'
    }, {
      field: 'createTime',
      compare: '>=',
      value: date
    }, {
      field: 'createTime',
      compare: '<',
      value: new Date(year, month + 1)
    }, {
      field: 'clientKey',
      compare: 'in',
      value: clients.map(client => client.clientKey)
    }]
    return dbAccessor.queryWithPredicates(predicates, ['systemTransactions'])
      .then(docs => {
        return docs.docs.map(item => {
          const { createTime, lastModifiedTime, ...rest } = item.data()
          return {
            ...rest,
            createTime: createTime.toDate(),
            lastModifiedTime: lastModifiedTime.toDate()
          }
        })
      })
  }
}

export const getters = {
  userLoading (state) {
    return state.userLoading
  },
  returnPackageCache (state) {
    return state.returnPackageCache
  },
  user (state) {
    return state.user
  },
  uid (state, getters) {
    return getters.operator.uid
  },
  sites (state) {
    return state.sites
  },
  userExtra (state) {
    return state.userExtra
  },
  mainAccount (state) {
    return state.mainAccount
  },
  isRegularUser () {
    return state.userExtra && state.userExtra.role === 1
  },
  activeOrganization (state) {
    return state.userExtra && state.userExtra.organizations && state.userExtra.organizations[0]
  },
  activeWorkfor (state) {
    return state.userExtra && state.userExtra.workfor
  },
  activeWarehouse (state) {
    return state.userExtra && state.userExtra.warehouses && state.userExtra.warehouses[0]
  },
  tenantWorkFor (state) {
    return state.tenantWorkFor
  },
  tenantRequested (state) {
    return state.tenantRequested
  },
  tenantsForUser (state) {
    let tenantWorkFor = state.tenantWorkFor
    let tenantRequested = state.tenantRequested
    if (tenantWorkFor && tenantWorkFor.length > 0) {
      let tenants = tenantWorkFor.map(tenant => {
        let {status, tenantKey, tenantName} = tenant
        return {
          tenantKey,
          tenantName,
          status
        }
      })
      return [...tenants, ...tenantRequested]
    } else {
      return [...tenantRequested]
    }
  },
  tasks (state) {
    return state.tasks
  },
  historyTasks (state) {
    return state.historyTasks
  },
  balanceForUser (state) {
    return state.balanceForUser
  },
  paymentRequests (state) {
    return state.paymentRequests
  },
  paymentMethods (state) {
    return state.paymentMethods
  },
  allTenantLimitedInfo (state) {
    return state.allTenantLimitedInfo
  },
  allWarehouseLimitedInfo (state) {
    return state.allWarehouseLimitedInfo
  },
  warehousesForUser (state) {
    return state.warehousesForUser
  },
  paymentRequestFilterItems (state) {
    return state.paymentRequestFilterItems
  },
  userInventory (state) {
    return state.userInventory
  },
  announcementsMap (state) {
    return state.announcementsMap
  },
  adminAnnouncements (state) {
    return state.adminAnnouncements
  },
  notifications (state) {
    return state.notifications
  },
  localSettings (state) {
    return state.localSettings
  },
  popupStack (state) {
    return state.popupStack
  },
  superUsers (state) {
    return state.superUsers
  },
  labelStatistics (state) {
    return state.labelStatistics
  },
  abnormalStatistics (state) {
    return state.abnormalStatistics
  },
  applicationList (state) {
    return state.applicationList
  },
  featureApplication (state) {
    return state.featureApplication
  },
  departments (state) {
    return state.departments
  },
  commonCommodities (state) {
    return state.commonCommodities
  },
  currentTask (state) {
    return state.currentTask
  },
  operator (state) {
    return state.mainAccount || state.user || state.userExtra
  }
}

export default {
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/user/index.js